/**
 * Scroll Indicator — Alpine.js Store
 * Tracks which homepage section is currently visible and provides navigation.
 */
export default function registerScrollIndicator(Alpine) {
    Alpine.store('scrollIndicator', {
        // State
        visible: false,
        name: '',
        number: '01',
        theme: 'light',
        currentId: '',
        sections: [],
        currentIndex: 0,

        init() {
            this.scan();
            this.update();
            let ticking = false;
            window.addEventListener('scroll', () => {
                if (!ticking) {
                    requestAnimationFrame(() => {
                        this.update();
                        ticking = false;
                    });
                    ticking = true;
                }
            }, { passive: true });
        },

        /**
         * Scan the DOM for all homepage sections.
         */
        scan() {
            this.sections = Array.from(
                document.querySelectorAll('[data-section]')
            ).map((el, i) => ({
                id: el.id,
                name: el.dataset.section,
                theme: el.dataset.sectionTheme || 'light',
                number: String(i + 1).padStart(2, '0'),
                el,
            }));
        },

        /**
         * Determine the currently visible section based on scroll position.
         */
        update() {
            if (!this.sections.length) return;

            const scrollY = window.scrollY;
            const viewportH = window.innerHeight;

            // Show indicator only after scrolling past half the first section
            this.visible = scrollY > viewportH * 0.3;

            // Find the section whose top is closest to the viewport center
            const center = scrollY + viewportH / 2;
            let closest = 0;
            let minDist = Infinity;

            this.sections.forEach((s, i) => {
                const rect = s.el.getBoundingClientRect();
                const sectionCenter = scrollY + rect.top + rect.height / 2;
                const dist = Math.abs(center - sectionCenter);
                if (dist < minDist) {
                    minDist = dist;
                    closest = i;
                }
            });

            const section = this.sections[closest];
            this.currentIndex = closest;
            this.name = section.name;
            this.number = section.number;
            this.theme = section.theme;
            this.currentId = section.id;
        },

        /**
         * Navigate to previous section.
         */
        prev() {
            const target = Math.max(0, this.currentIndex - 1);
            this.scrollTo(target);
        },

        /**
         * Navigate to next section.
         */
        next() {
            const target = Math.min(this.sections.length - 1, this.currentIndex + 1);
            this.scrollTo(target);
        },

        /**
         * Smooth scroll to a section by index.
         */
        scrollTo(index) {
            const section = this.sections[index];
            if (section) {
                section.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
    });
}
